// react
import { useEffect, useState } from 'react';

// types
import { Benefit, Contract, ExpenseType, Transaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// forms
import { FormAddComment } from '../../forms/FormAddComment/FormAddComment';

// ui
import { transactionIconTypes } from '../../ui/IconCircle/IconCircle';

// utils
import { formatCurrency } from '../../../utils/currency';

// props
export interface DialogAddCommentProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export const DialogAddComment = ({ isOpen, onClose, onSuccess }: DialogAddCommentProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogContract = searchParams.get('dialogContract');
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// state
	const [contract, setContract] = useState<Contract | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [transaction, setTransaction] = useState<Transaction | null>(null);

	// fetch transaction
	useFetch({
		isEnabled: Boolean(paramDialogTransaction && isOpen),
		url: `${process.env.REACT_APP_API_URL}/transactions/${paramDialogTransaction}?fields=amount,benefit,currency,expenseType,id,quantity,type&populate=benefit,expenseType`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransaction(res.data[0]);
			}
		},
	});

	// fetch contract
	useFetch({
		isEnabled: Boolean(paramDialogContract && isOpen),
		url: `${process.env.REACT_APP_API_URL}/contracts/${paramDialogContract}?fields=id,num`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setContract(res.data[0]);
			}
		},
	});

	let IconCircleProps: DialogActionsProps['IconCircleProps'];
	let subtitle: DialogActionsProps['subtitle'];

	if (transaction) {
		IconCircleProps = {
			type: transactionIconTypes[transaction.type],
		};

		if (transaction.type === 'NON_CASH_BENEFIT') {
			if (transaction.benefit) {
				subtitle = {
					children: `${transaction.quantity} ${(transaction.benefit as Benefit).name}`,
				};
			}
		} else {
			const amount = formatCurrency({
				currency: transaction.currency,
				value: transaction.amount,
			});

			subtitle = {
				children: amount,
			};

			if (transaction.expenseType) {
				subtitle.children = `${amount} (${(transaction.expenseType as ExpenseType).name})`;
			}
		}
	} else if (contract) {
		IconCircleProps = {
			type: 'contract',
		};

		subtitle = {
			children: contract.num,
		};
	}

	// reset transaction and contract when closed
	useEffect(() => {
		setTransaction(null);
		setContract(null);
	}, [isOpen]);

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: onClose,
				},
				{
					children: t('addComment'),
					form: 'form-add-comment',
					loading: isLoading,
					type: 'submit',
					variant: 'contained',
				},
			]}
			alignItems="center"
			hasClose={true}
			IconCircleProps={IconCircleProps}
			isOpen={isOpen}
			onClose={onClose}
			subtitle={subtitle}
			title={{
				children: t('addComment'),
			}}>
			<FormAddComment onClose={onClose} onSuccess={onSuccess} setIsLoading={setIsLoading} />
		</DialogActions>
	);
};
